import { nameFromUrl } from '@/utils/string';
import { DATA_POINTS, TYPES } from './constants';

const isImageAsset = (value) => {
  const isPlainObject =
    value !== null && typeof value === 'object' && !Array.isArray(value);

  if (!isPlainObject) return false;

  const hasUrl = typeof value.url === 'string';
  const hasDimensions =
    typeof value.dimension?.width === 'number' &&
    typeof value.dimension?.height === 'number';
  const isImage =
    !value.content_type || value.content_type.startsWith('image/');

  return hasUrl && hasDimensions && isImage;
};

// Keys injected by extendBlocks that contain non-Contentstack payloads
// and should not be traversed during image extraction.
const SKIP_KEYS = new Set(['shopifyProduct']);

export const extractImages = (dataBlocks, seen = new Set()) => {
  if (!dataBlocks || typeof dataBlocks !== 'object') return [];

  if (isImageAsset(dataBlocks)) {
    if (seen.has(dataBlocks.url)) return [];
    seen.add(dataBlocks.url);
    return [dataBlocks];
  }

  const entries = Array.isArray(dataBlocks)
    ? dataBlocks
    : Object.entries(dataBlocks)
        .filter(([key]) => !SKIP_KEYS.has(key))
        .map(([, value]) => value);

  return entries.flatMap((item) => extractImages(item, seen));
};

const CURRENT_YEAR = new Date().getFullYear();

export const imageProperty = (
  name,
  url,
  { description, width, height, encodingFormat, copyrightYear } = {}
) => {
  const resolvedName = (typeof name === 'string' && name.trim()) || nameFromUrl(url);

  const resolvedCopyrightYear = copyrightYear ?? CURRENT_YEAR;

  return {
    '@type': TYPES.image_object,
    contentUrl: url,
    url: url,
    ...(resolvedName && { name: resolvedName }),
    ...(description && { description }),
    ...(typeof width === 'number' && { width }),
    ...(typeof height === 'number' && { height }),
    ...(encodingFormat && { encodingFormat }),
    license: DATA_POINTS.url.terms_of_use,
    acquireLicensePage: DATA_POINTS.url.contact_us,
    creditText: DATA_POINTS.name,
    creator: {
      '@type': TYPES.organization,
      '@id': `${DATA_POINTS.url.base}/#organization`,
      name: DATA_POINTS.name,
    },
    copyrightHolder: {
      '@type': TYPES.organization,
      '@id': `${DATA_POINTS.url.base}/#organization`,
      name: DATA_POINTS.name,
    },
    copyrightYear: resolvedCopyrightYear,
    copyrightNotice: `© ${resolvedCopyrightYear} ${DATA_POINTS.copyrightHolder}. All rights reserved.`,
  };
};

export const imagePropertyFromAsset = (asset) => {
  if (!asset?.url) return null;
  return imageProperty(asset.title, asset.url, {
    description: asset.description,
    width: asset.dimension?.width,
    height: asset.dimension?.height,
    encodingFormat: asset.content_type,
  });
};

export const buildImageSchemas = (metadata, images = []) => {
  const ogImage = imagePropertyFromAsset(metadata?.og_image);
  const blockImageSchemas = images.map(imagePropertyFromAsset).filter(Boolean);
  const blockUrls = new Set(blockImageSchemas.map((img) => img.url));
  return [
    ...(ogImage && !blockUrls.has(ogImage.url) ? [ogImage] : []),
    ...blockImageSchemas,
  ];
};
